import type { ReactNode } from "react";
import { cardStyles } from "../core/Card.styles.js";
import { Text } from "../core/Text.js";
import { cn } from "../foundations/cn.js";
import { Stage } from "../moments/Stage.js";

export interface ClosingBandProps {
  /** The store's mark, above the headline. */
  mark?: ReactNode;
  title: string;
  body?: string;
  /** One brand button. */
  action: ReactNode;
  /** Art at the band's sides; with `arrange="start"`, the stage behind the words (a PackParade). */
  media?: ReactNode;
  /**
   * `centered`: the words in the middle of a lit room, art at its sides (hidden on phones).
   * `start`: the words at the band's start over a scrim, the art playing behind them, under the
   * words on phones and beside them from md.
   */
  arrange?: "centered" | "start";
  className?: string;
}

/** The last word before the footer: a lit room framing one button. */
export function ClosingBand({
  mark,
  title,
  body,
  action,
  media,
  arrange = "centered",
  className,
}: ClosingBandProps) {
  const start = arrange === "start";
  return (
    <section className={cn("mx-auto w-full max-w-[1440px] px-4 md:px-8 lg:px-20", className)}>
      <div
        className={cn(
          cardStyles({ radius: "3xl" }),
          "relative isolate flex flex-col gap-5 overflow-hidden",
          start
            ? "min-h-[26rem] items-start justify-start p-6 text-left md:min-h-[20rem] md:justify-center md:p-10 lg:p-12"
            : "items-center px-6 py-16 text-center md:py-24",
        )}
      >
        <Stage className="-z-10" />
        {media && (
          <div
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-0",
              start ? "-z-[5]" : "-z-10 max-md:hidden",
            )}
          >
            {media}
          </div>
        )}
        {start && media && <div aria-hidden className="pack-parade-scrim -z-[4]" />}
        {mark}
        <div
          className={cn(
            "flex flex-col",
            start ? "max-w-[34rem] items-start gap-3" : "items-center gap-5",
          )}
        >
          <Text as="h2" variant="display-band" tone="primary" className="max-md:text-display-xl2">
            {title}
          </Text>
          {body && (
            <Text variant="body-b1" tone="secondary" className={cn(start && "max-w-[40ch]")}>
              {body}
            </Text>
          )}
        </div>
        <div className="pt-2">{action}</div>
      </div>
    </section>
  );
}
